import React from 'react';
import { Form, Field } from 'react-final-form';
import { FORM_ERROR } from 'final-form';
import { useDispatch } from 'react-redux';
import { Prompt } from '../../../../store/prompt/types';
import { setPrompts, setError } from '../../../../store/prompt/slice';
import { createPrompt, getAllPrompts, updatePrompt } from '../../../../api/promptApi';
import TextInput from '../../../../components/UI/TextInput';
import TextArea from '../../../../components/UI/TextArea';
import Button from '../../../../components/UI/Button';
import styles from './PromptForm.module.scss';

interface PromptFormProps {
  initialValues?: Prompt;
  onSubmit: () => void;
  mode: 'create' | 'edit';
}

interface FormValues {
  name: string;
  description: string;
  content: string;
  tag?: string;
  top_k?: number;
  top_p?: number;
  temperature?: number;
  max_output_tokens?: number;
  thinking_budget?: number;
  tuneable: boolean;
}

const required = (value: string) => (value ? undefined : 'Required');

const PromptForm: React.FC<PromptFormProps> = ({ initialValues, onSubmit, mode }) => {
  const dispatch = useDispatch();

  const handleSubmit = async (values: FormValues) => {
    try {
      if (mode === 'create') {
        await createPrompt(values as Prompt);
      } else if (initialValues?.id) {
        await updatePrompt(initialValues.id, { ...values, id: initialValues.id } as Prompt);
      }

      const updatedPrompts = await getAllPrompts();
      dispatch(setPrompts(updatedPrompts));
      dispatch(setError(null));
      onSubmit();
    } catch (err) {
      dispatch(setError(err instanceof Error ? err.message : 'Failed to save prompt'));
      return { [FORM_ERROR]: err instanceof Error ? err.message : 'Failed to save prompt' };
    }
  };

  return (
    <Form<FormValues>
      onSubmit={handleSubmit}
      initialValues={
        initialValues || {
          name: '',
          description: '',
          content: '',
          tag: '',
          top_k: undefined,
          top_p: undefined,
          temperature: undefined,
          max_output_tokens: undefined,
          thinking_budget: undefined,
          tuneable: true,
        }
      }
      render={({ handleSubmit, submitting, submitError }) => (
        <form onSubmit={handleSubmit} className={styles.form}>
          {mode === 'create' && (
            <Field name="name" validate={required}>
              {({ input, meta }) => (
                <TextInput
                  {...input}
                  label="Name"
                  required
                  fullWidth
                  error={meta.touched && meta.error ? meta.error : undefined}
                />
              )}
            </Field>
          )}
          <Field name="tag">{({ input }) => <TextInput {...input} label="Tag" fullWidth />}</Field>
          <Field name="description" validate={required}>
            {({ input, meta }) => (
              <TextInput
                {...input}
                label="Description"
                required
                fullWidth
                error={meta.touched && meta.error ? meta.error : undefined}
              />
            )}
          </Field>
          <Field name="content" validate={required}>
            {({ input, meta }) => (
              <TextArea
                {...input}
                label="Content"
                required
                fullWidth
                rows={20}
                error={meta.touched && meta.error ? meta.error : undefined}
              />
            )}
          </Field>
          {(initialValues?.tuneable ?? true) && (
            <>
              <Field name="temperature">
                {({ input }) => (
                  <TextInput
                    {...input}
                    type="number"
                    step="0.01"
                    min="0"
                    max="1"
                    label="Temperature"
                    fullWidth
                    placeholder="Default: 0.7"
                  />
                )}
              </Field>
              <Field name="top_p">
                {({ input }) => (
                  <TextInput
                    {...input}
                    type="number"
                    step="0.01"
                    min="0"
                    max="1"
                    label="Top P"
                    fullWidth
                    placeholder="Default: 0.95"
                  />
                )}
              </Field>
              <Field name="top_k">
                {({ input }) => (
                  <TextInput
                    {...input}
                    type="number"
                    step="1"
                    min="0"
                    label="Top K"
                    fullWidth
                    placeholder="Default: 40"
                  />
                )}
              </Field>
              <Field name="max_output_tokens">
                {({ input }) => (
                  <TextInput
                    {...input}
                    type="number"
                    step="1"
                    min="0"
                    label="Max Output Tokens"
                    fullWidth
                    placeholder="Default: 8192"
                  />
                )}
              </Field>
              <Field name="thinking_budget">
                {({ input }) => (
                  <TextInput
                    {...input}
                    type="number"
                    step="1"
                    min="0"
                    label="Thinking Budget"
                    fullWidth
                  />
                )}
              </Field>
            </>
          )}
          {submitError && <div className={styles.error}>{submitError}</div>}
          <div className={styles.actions}>
            <Button type="submit" disabled={submitting} loading={submitting} fullWidth>
              {mode === 'create' ? 'Create Prompt' : 'Save Changes'}
            </Button>
          </div>
        </form>
      )}
    />
  );
};

export default PromptForm;
